import { z } from 'zod'

import { MarketingCampaignStatus } from 'common/settings/components/SettingsPanel/MessengerSection/MarketingCampaigns/types'

import {
  IsCustomAudienceActionTokenErrorSchema,
  IsFbAccountSettingsErrorSchema,
  IsFbmmCreateCampaignBudgetErrorSchema,
} from './errors'

const CreateFbmmCampaignRequestSchema = z
  .object({
    ad_account_id: z.string(),
    name: z.string(),
    daily_budget: z.number().optional(),
    lifetime_budget: z.number().optional(),
    currency: z.string(),
  })
  .refine(
    (data) => {
      const hasDaily = data.daily_budget !== undefined
      const hasLifetime = data.lifetime_budget !== undefined
      return hasDaily !== hasLifetime // XOR: exactly one must be true
    },
    {
      message: 'Exactly one of daily_budget or lifetime_budget must be provided',
      path: ['daily_budget'], // This will show the error on the daily_budget field
    },
  )

const DeliveryEstimateSchema = z.object({
  estimate_delivery_lower_bound: z.number(),
  estimate_delivery_upper_bound: z.number(),
})

const AdAccountsListResponseSchema = z.object({
  data: z.array(
    z.object({
      account_id: z.string(),
      account_status: z.number(),
      currency: z.string(),
      id: z.string(),
      min_daily_budget: z.number(),
      name: z.string(),
      timezone_id: z.number(),
      timezone_name: z.string(),
      timezone_offset_hours_utc: z.number(),
      tos_accepted: z.boolean().nullable(),
    }),
  ),
})

export const AdsSchemas = {
  getAdAccountsList: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: AdAccountsListResponseSchema,
  },
  createFbmmCampaign: {
    request: CreateFbmmCampaignRequestSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: z.object({
      data: z.object({
        id: z.string(),
      }),
    }),
    errors: {
      IsFbmmCreateCampaignBudgetError: IsFbmmCreateCampaignBudgetErrorSchema,
    },
  },
  getFbmmCampaignsList: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: z.object({
      campaigns: z.array(
        z
          .object({
            id: z.string(),
            status: z.enum(MarketingCampaignStatus),
            account_id: z.string(),
            account_name: z.string(),
            currency: z.string(),
            name: z.string(),
            daily_budget: z.number().optional(),
            lifetime_budget: z.number().optional(),
          })
          .refine(
            (data) => {
              const hasDaily = data.daily_budget !== undefined
              const hasLifetime = data.lifetime_budget !== undefined
              return hasDaily !== hasLifetime // XOR: exactly one must be true
            },
            {
              message: 'Exactly one of daily_budget or lifetime_budget must exist',
              path: ['daily_budget'], // This will show the error on the daily_budget field
            },
          ),
      ),
    }),
    errors: {
      IsFbAccountSettingsError: IsFbAccountSettingsErrorSchema,
    },
  },
  getFbmmEstimations: {
    request: z.undefined(),
    query: z
      .object({
        ad_account_id: z.string(),
        daily_budget: z.number().optional(),
        lifetime_budget: z.number().optional(),
      })
      .refine((data) => {
        const hasDaily = data.daily_budget !== undefined
        const hasLifetime = data.lifetime_budget !== undefined
        return hasDaily !== hasLifetime // XOR: exactly one must be true
      }),
    path: z.undefined(),
    response: DeliveryEstimateSchema,
  },
  deleteFbmmCampaign: {
    request: z.object({
      campaign_id: z.string(),
    }),
    query: z.undefined(),
    path: z.undefined(),
    response: z.undefined(),
  },

  // Custom Audiences Action for IG

  fetchAdAccountsForIg: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: AdAccountsListResponseSchema,
    errors: {
      IsCustomAudienceActionTokenError: IsCustomAudienceActionTokenErrorSchema,
    },
  },
  fetchCustomAudiencesForIg: {
    request: z.undefined(),
    query: z.object({
      ad_account_id: z.string(),
    }),
    path: z.undefined(),
    response: z.object({
      data: z.array(
        z.object({
          account_id: z.string(),
          customer_file_source: z.string().nullable(),
          data_source: z.object({
            type: z.string(),
            sub_type: z.string(),
            creation_params: z.string(),
          }),
          name: z.string(),
          subtype: z.string(),
          facebook_page_id: z.string().nullable(),
          is_addable: z.boolean(),
        }),
      ),
    }),
  },
  fetchCustomAudienceUserActionNamesForIg: {
    request: z.undefined(),
    query: z.object({
      ad_account_id: z.string(),
      custom_audience_id: z.string(),
      user_id: z.number(),
    }),
    path: z.undefined(),
    response: z.object({
      ad_account_name: z.string(),
      custom_audience_name: z.string(),
    }),
  },
  getIsOptedIn: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: z.object({
      is_opted_in: z.boolean(),
      update_last_interaction_required: z.boolean(),
      subscribe_optin_payload: z.string(),
    }),
  },
}

export type AdAccountsListResponse = z.infer<typeof AdAccountsListResponseSchema>
export type AdAccountFromApi = AdAccountsListResponse['data'][number]

export type CreateFbmmCampaignRequest = z.infer<typeof CreateFbmmCampaignRequestSchema>
export type DeliveryEstimate = z.infer<typeof DeliveryEstimateSchema> | string | undefined
